import type { JSX } from "react";
import { ListIllustration } from "vega-illustrations";

import { useGetCustomHeaders } from "@vega-api/custom/stores/useGetCustomHeaders";
import { FeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { useGetDealOpportunitiesForLimitedPartnerV2 } from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { m } from "@vega-core/paraglide/messages";
import { LPPageContainerWithNavigation } from "@vega-modules/components/PageContainerWithNavigation/PageContainerWithNavigation";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";

import {
  type CoInvestmentItem,
  CoInvestmentRow,
} from "../components/CoInvestmentRow";
import { CoInvestmentsListSkeleton } from "../components/CoInvestmentsListSkeleton";
import { useCoInvestmentFilters } from "../hooks/useCoInvestmentFilters";

export const CoInvestmentsListPage = () => {
  const { limitedPartnerId } = useGetCustomHeaders();

  const { isLoading: isFeatureFlagLoading } = useFeatureFlagGuard(
    FeatureFlag.LP_CO_INVESTMENT_UI,
    CommonPaths.Home,
  );

  const { RenderFilters, queryParams, hasActiveFilters } =
    useCoInvestmentFilters();

  const {
    data: dealOpportunities,
    isLoading: isDealOpportunitiesLoading,
    error: dealOpportunitiesError,
  } = useGetDealOpportunitiesForLimitedPartnerV2(
    {
      limitedPartnerId: limitedPartnerId!,
      ...queryParams,
    },
    {
      query: {
        select: (response) => response.data,
      },
    },
  );

  const hasError = !!dealOpportunitiesError;
  const showEmptyCard =
    (dealOpportunities?.length === 0 && !isDealOpportunitiesLoading) ||
    hasError;

  const getEmptyCardState = (): {
    title: string;
    description: string;
    illustration: JSX.Element;
  } => {
    if (hasError) {
      return {
        title: m.navTitles_coInvestments(),
        description: m.errors_unexpectedError(),
        illustration: <ListIllustration />,
      };
    }

    return {
      title: m.navTitles_coInvestments(),
      description: hasActiveFilters
        ? m.coInvestments_noDealsMatchFilters()
        : m.fund_please_wait_while_our_team_sets_this_up_for_you(),
      illustration: <ListIllustration />,
    };
  };

  if (isFeatureFlagLoading) {
    return null;
  }

  return (
    <LPPageContainerWithNavigation showNavbar={false}>
      <div className="p-4 lg:p-8">
        <h4 className="text-text/primary heading-4">
          {m.navTitles_coInvestments()}
        </h4>
        <p className="text-text/secondary body-base-regular">
          {m.coInvestments_list_description()}
        </p>

        <div className="mt-6">
          {RenderFilters}

          {isDealOpportunitiesLoading ? <CoInvestmentsListSkeleton /> : null}
          {!isDealOpportunitiesLoading && showEmptyCard ? (
            <EmptyCard {...getEmptyCardState()} />
          ) : null}
          {!isDealOpportunitiesLoading && !showEmptyCard && (
            <div className="overflow-hidden">
              {dealOpportunities?.map((investment: CoInvestmentItem) => (
                <CoInvestmentRow
                  item={investment}
                  key={investment.dealOpportunity.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </LPPageContainerWithNavigation>
  );
};
